import { useState, useMemo } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { LogOut, Clock, TrendingUp, AlertTriangle, Loader2, UserPlus, Users, FileText, Search, Calendar } from "lucide-react";
import { useVisitors, useUpdateVisitorExit, useVisitorsByDateRange } from "@/hooks/useVisitors";
import { useAuth } from "@/hooks/useAuth";
import { useCondominium } from "@/hooks/useCondominium";
import { format, isToday, differenceInMinutes, startOfMonth, subDays } from "date-fns";
import NovoRegistroAcesso from "@/components/acessos/NovoRegistroAcesso";
import { generateVisitorReportPDF } from "@/utils/pdfGenerator";

const AVATAR_COLORS = ["bg-violet-500", "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500"];
const getInitials = (name: string) => name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();
const getAvatarColor = (name: string) => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const formatDuration = (entryTime: string | null, exitTime: string | null) => {
  if (!entryTime) return "-";
  const end = exitTime ? new Date(exitTime) : new Date();
  const mins = differenceInMinutes(end, new Date(entryTime));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${m.toString().padStart(2, "0")}min`;
};

const Acessos = () => {
  const [showNewRegistro, setShowNewRegistro] = useState(false);
  const { data: visitors = [], isLoading } = useVisitors();
  const exitMutation = useUpdateVisitorExit();
  const { profile } = useAuth();
  const { data: condominium } = useCondominium();

  // History filters
  const today = format(new Date(), "yyyy-MM-dd");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [searchDates, setSearchDates] = useState<{ start: string; end: string }>({ start: today, end: today });

  const { data: historyVisitors = [], isLoading: isLoadingHistory } = useVisitorsByDateRange(
    searchDates.start,
    searchDates.end
  );

  const activeVisitors = useMemo(
    () => visitors.filter(v => !v.exit_time && v.entry_time && isToday(new Date(v.created_at!))),
    [visitors]
  );

  const avgMinutes = useMemo(() => {
    if (activeVisitors.length === 0) return 0;
    const total = activeVisitors.reduce((sum, v) => sum + differenceInMinutes(new Date(), new Date(v.entry_time!)), 0);
    return Math.round(total / activeVisitors.length);
  }, [activeVisitors]);

  const pendingExits = useMemo(
    () => visitors.filter(v => !v.exit_time && v.entry_time && !isToday(new Date(v.created_at!))).length,
    [visitors]
  );

  const handleSearch = () => {
    setSearchDates({ start: startDate, end: endDate });
  };

  const handleQuickFilter = (type: "today" | "week" | "month") => {
    const now = new Date();
    let start: string;
    if (type === "today") {
      start = today;
    } else if (type === "week") {
      start = format(subDays(now, 7), "yyyy-MM-dd");
    } else {
      start = format(startOfMonth(now), "yyyy-MM-dd");
    }
    setStartDate(start);
    setEndDate(today);
    setSearchDates({ start, end: today });
  };

  const handleExportPDF = async () => {
    const mapped = historyVisitors.map(v => ({
      name: v.name,
      document: v.document,
      destination: v.notes || v.unit?.number || null,
      entry_time: v.entry_time,
      exit_time: v.exit_time,
      created_at: v.created_at,
      status: v.status,
    }));
    await generateVisitorReportPDF(
      mapped,
      { name: condominium?.name || "Condomínio", address: condominium?.address || undefined },
      searchDates.start,
      searchDates.end
    );
  };

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Controle de Acessos
              </h1>
              <p className="text-xs text-muted-foreground">Controle de fluxo em tempo real no edifício</p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">
              {activeVisitors.length} Presentes
            </Badge>
          </div>
          <Button onClick={() => setShowNewRegistro(true)} className="gap-1.5 h-9 text-sm">
            <UserPlus className="h-4 w-4" />
            Novo Registro
          </Button>
        </div>

        <Tabs defaultValue="ativos" className="w-full">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="ativos" className="gap-1.5">
              <Users className="h-4 w-4" />
              Visitantes Ativos
            </TabsTrigger>
            <TabsTrigger value="historico" className="gap-1.5">
              <FileText className="h-4 w-4" />
              Histórico / Relatório
            </TabsTrigger>
          </TabsList>

          {/* Tab: Visitantes Ativos */}
          <TabsContent value="ativos" className="space-y-4">
            <Card>
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">VISITANTE</TableHead>
                        <TableHead className="text-xs hidden sm:table-cell">DESTINO</TableHead>
                        <TableHead className="text-xs">ENTRADA</TableHead>
                        <TableHead className="text-xs hidden sm:table-cell">STATUS</TableHead>
                        <TableHead className="text-xs text-right">AÇÕES</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeVisitors.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-xs text-muted-foreground py-8">
                            Nenhum visitante ativo no momento
                          </TableCell>
                        </TableRow>
                      ) : activeVisitors.map(v => (
                        <TableRow key={v.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-7 w-7">
                                <AvatarFallback className={`${getAvatarColor(v.name)} text-white text-[10px]`}>
                                  {getInitials(v.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="text-xs font-medium">{v.name}</p>
                                <p className="text-[10px] text-muted-foreground">{v.document || "—"}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs hidden sm:table-cell">{v.notes || v.unit?.number || "—"}</TableCell>
                          <TableCell className="text-xs">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              {v.entry_time ? format(new Date(v.entry_time), "HH:mm") : "—"}
                            </div>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">AUTORIZADO</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="destructive"
                              size="sm"
                              className="h-7 text-xs gap-1"
                              disabled={exitMutation.isPending}
                              onClick={() => exitMutation.mutate(v.id)}
                            >
                              {exitMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogOut className="h-3 w-3" />}
                              <span className="hidden sm:inline">Saída Rápida</span>
                              <span className="sm:hidden">Saída</span>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {activeVisitors.length > 0 && (
              <p className="text-xs text-muted-foreground text-center">
                Mostrando {activeVisitors.length} visitante{activeVisitors.length !== 1 ? "s" : ""} ativo{activeVisitors.length !== 1 ? "s" : ""}
              </p>
            )}

            {/* Metrics */}
            <div className="grid grid-cols-3 gap-3">
              <Card>
                <CardContent className="p-3 text-center">
                  <Clock className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
                  <p className="text-lg font-bold">{avgMinutes} min</p>
                  <p className="text-[10px] text-muted-foreground">Tempo Médio</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <TrendingUp className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
                  <p className="text-lg font-bold">{activeVisitors.length}</p>
                  <p className="text-[10px] text-muted-foreground">Fluxo/Hora</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <AlertTriangle className="h-4 w-4 mx-auto text-amber-500 mb-1" />
                  <p className="text-lg font-bold">{pendingExits}</p>
                  <p className="text-[10px] text-muted-foreground">Pendências</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab: Histórico / Relatório */}
          <TabsContent value="historico" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Data Início</label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      className="h-9 text-sm w-40"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Data Fim</label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      className="h-9 text-sm w-40"
                    />
                  </div>
                  <Button onClick={handleSearch} className="h-9 gap-1.5 text-sm">
                    <Search className="h-4 w-4" />
                    Buscar
                  </Button>
                  <Button onClick={handleExportPDF} variant="outline" className="h-9 gap-1.5 text-sm" disabled={historyVisitors.length === 0}>
                    <FileText className="h-4 w-4" />
                    Exportar PDF
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" className="h-7 text-xs" onClick={() => handleQuickFilter("today")}>
                    <Calendar className="h-3 w-3 mr-1" />
                    Hoje
                  </Button>
                  <Button variant="secondary" size="sm" className="h-7 text-xs" onClick={() => handleQuickFilter("week")}>
                    Últimos 7 dias
                  </Button>
                  <Button variant="secondary" size="sm" className="h-7 text-xs" onClick={() => handleQuickFilter("month")}>
                    Mês Atual
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <Card>
              <CardContent className="p-0">
                {isLoadingHistory ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">VISITANTE</TableHead>
                        <TableHead className="text-xs hidden sm:table-cell">DOCUMENTO</TableHead>
                        <TableHead className="text-xs hidden md:table-cell">DESTINO</TableHead>
                        <TableHead className="text-xs">ENTRADA</TableHead>
                        <TableHead className="text-xs">SAÍDA</TableHead>
                        <TableHead className="text-xs hidden sm:table-cell">PERMANÊNCIA</TableHead>
                        <TableHead className="text-xs">STATUS</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyVisitors.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-xs text-muted-foreground py-8">
                            Nenhum visitante encontrado no período selecionado
                          </TableCell>
                        </TableRow>
                      ) : historyVisitors.map(v => (
                        <TableRow key={v.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-7 w-7">
                                <AvatarFallback className={`${getAvatarColor(v.name)} text-white text-[10px]`}>
                                  {getInitials(v.name)}
                                </AvatarFallback>
                              </Avatar>
                              <p className="text-xs font-medium">{v.name}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs hidden sm:table-cell">{v.document || "—"}</TableCell>
                          <TableCell className="text-xs hidden md:table-cell">{v.notes || v.unit?.number || "—"}</TableCell>
                          <TableCell className="text-xs">
                            {v.entry_time ? format(new Date(v.entry_time), "dd/MM HH:mm") : "—"}
                          </TableCell>
                          <TableCell className="text-xs">
                            {v.exit_time ? format(new Date(v.exit_time), "dd/MM HH:mm") : "—"}
                          </TableCell>
                          <TableCell className="text-xs hidden sm:table-cell">
                            {formatDuration(v.entry_time, v.exit_time)}
                          </TableCell>
                          <TableCell>
                            {v.exit_time ? (
                              <Badge variant="secondary" className="text-[10px]">Concluído</Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Ativo</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {historyVisitors.length > 0 && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{historyVisitors.length} registro{historyVisitors.length !== 1 ? "s" : ""} encontrado{historyVisitors.length !== 1 ? "s" : ""}</span>
                <span>
                  {historyVisitors.filter(v => v.exit_time).length} saída{historyVisitors.filter(v => v.exit_time).length !== 1 ? "s" : ""} registrada{historyVisitors.filter(v => v.exit_time).length !== 1 ? "s" : ""}
                </span>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Modal Novo Registro */}
        <Dialog open={showNewRegistro} onOpenChange={setShowNewRegistro}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Novo Registro de Visitante</DialogTitle>
            </DialogHeader>
            <NovoRegistroAcesso onSuccess={() => setShowNewRegistro(false)} />
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Acessos;
